import React, { useState, useRef, useEffect, useCallback } from 'react';
import apiClient from '../../api/client';
import {
    FiSend,
    FiPlus,
    FiMic,
    FiTrendingUp,
    FiTrendingDown,
    FiBarChart2,
    FiAlertCircle,
    FiGlobe,
    FiDollarSign,
    FiActivity,
    FiPieChart,
    FiSearch,
    FiX,
    FiChevronDown,
    FiChevronUp,
    FiMaximize2,
    FiMinimize2,
    FiZap,
    FiTarget,
    FiClock,
    FiCheckCircle,
    FiMessageSquare,
    FiTrash2,
    FiMenu,
    FiArrowDown
} from 'react-icons/fi';
import { showToast } from '../../utils/toast';
import { useAuthStore } from '../../stores';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    AreaChart,
    Area,
    BarChart,
    Bar,
    ComposedChart,
    Legend,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar
} from 'recharts';

// ========================================
// TYPE DEFINITIONS
// ========================================

interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    symbols?: string[];
    stockData?: StockData[];
    chartData?: { [symbol: string]: ChartDataPoint[] };
    isLoading?: boolean;
    status?: 'fetching_price' | 'analyzing_charts' | 'streaming';
    displayedContent?: string; // For streaming effect
    relatedQuestions?: string[]; // Backend: milte-julte follow-up questions
}

interface StockData {
    symbol: string;
    isForex?: boolean;
    quote?: {
        price: number;
        change: number;
        changePercent: number;
        dayHigh: number;
        dayLow: number;
        volume: number;
        marketCap: number;
        currency: string;
        exchange: string;
    };
    profile?: {
        name: string;
        sector: string;
        industry: string;
        description?: string;
    };
    financialStats?: {
        peRatio: number | null;
        eps: number | null;
        fiftyTwoWeekHigh: number | null;
        fiftyTwoWeekLow: number | null;
        targetMeanPrice: number | null;
        recommendationKey: string | null;
        debtToEquity: number | null;
        profitMargin: number | null;
    };
    technicalIndicators?: {
        sma20: number | null;
        sma50: number | null;
        sma100: number | null;
        sma200: number | null;
        rsi: number | null;
        macd: number | null;
        support: number | null;
        resistance: number | null;
        trend: string;
        rsiSignal: string;
    };
}

interface ChartDataPoint {
    date: string;
    timestamp: number;
    open: number;
    high: number;
    low: number;
    close: number;
    volume: number;
}

interface SearchResult {
    symbol: string;
    name: string;
    exchange: string;
    type: string;
}

interface Conversation {
    _id: string;
    title: string;
    preview: string;
    messageCount: number;
    lastActivityAt: string;
    createdAt: string;
}

// ========================================
// CUSTOM STYLES
// ========================================

const customStyles = `
@keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes slideIn {
    from { opacity: 0; transform: translateX(-20px); }
    to { opacity: 1; transform: translateX(0); }
}

@keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
}

@keyframes shimmer {
    0% { background-position: -1000px 0; }
    100% { background-position: 1000px 0; }
}

.animate-fadeIn {
    animation: fadeIn 0.5s ease-out;
}

.animate-slideIn {
    animation: slideIn 0.5s ease-out;
}

.gradient-border {
    position: relative;
    background: linear-gradient(to right, var(--surface-elevated), var(--surface-elevated));
    border-radius: 16px;
}

.gradient-border::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(135deg, var(--primary), var(--accent), var(--primary));
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
}

.glass-effect {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

.chart-container {
    background: linear-gradient(135deg, rgba(var(--primary-rgb), 0.05), rgba(var(--accent-rgb), 0.05));
    border-radius: 12px;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
}
@media (min-width: 640px) {
    .chart-container {
        border-radius: 16px;
        padding: 20px;
    }
}

.metric-card {
    background: linear-gradient(135deg, var(--surface), var(--surface-elevated));
    border-radius: 12px;
    padding: 16px;
    transition: all 0.3s ease;
    border: 1px solid var(--border);
}

.metric-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
    border-color: var(--primary);
}

.loading-shimmer {
    background: linear-gradient(90deg, var(--surface) 0px, var(--surface-elevated) 40px, var(--surface) 80px);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
}
`;

// ========================================
// HELPER FUNCTIONS
// ========================================

const formatNumber = (num: number | null | undefined, decimals = 2): string => {
    if (num === null || num === undefined) return '—';
    return num.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatLargeNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '—';
    if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    return `$${num.toLocaleString()}`;
};

/** Never show literal "undefined" or "null"; use dash for missing values. */
const safeDisplay = (v: unknown): string => {
    if (v == null || v === '') return '—';
    const s = String(v).trim();
    if (s === 'undefined' || s === 'null' || s === 'N/A') return '—';
    return s;
};

// ========================================
// ENHANCED COMPONENTS
// ========================================

// Comparison Chart Component
const ComparisonChart: React.FC<{ stocks: StockData[]; chartData: { [symbol: string]: ChartDataPoint[] } }> = ({ stocks, chartData }) => {
    const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M'>('1M');

    const filterDataByTimeframe = (data: ChartDataPoint[]) => {
        const now = Date.now();
        const days = { '1W': 7, '1M': 30, '3M': 90, '6M': 180 };
        const cutoff = now - days[timeframe] * 24 * 60 * 60 * 1000;
        return data.filter(d => d.timestamp * 1000 > cutoff);
    };

    // Normalize data to percentage change from start
    const normalizeData = () => {
        const normalizedData: any[] = [];
        const symbols = Object.keys(chartData);

        if (symbols.length === 0) return [];

        const filteredData = symbols.map(symbol => ({
            symbol,
            data: filterDataByTimeframe(chartData[symbol])
        }));

        const maxLength = Math.max(...filteredData.map(fd => fd.data.length));

        for (let i = 0; i < maxLength; i++) {
            const dataPoint: any = { index: i };

            filteredData.forEach(({ symbol, data }) => {
                if (i < data.length && data[0]) {
                    const basePrice = data[0].close;
                    const currentPrice = data[i].close;
                    const percentChange = ((currentPrice - basePrice) / basePrice) * 100;
                    dataPoint[symbol] = percentChange;
                    dataPoint.date = data[i].date;
                }
            });

            if (dataPoint.date) {
                normalizedData.push(dataPoint);
            }
        }

        return normalizedData;
    };

    const data = normalizeData();
    const colors = ['#6366f1', '#ec4899', '#10b981', '#f59e0b', '#8b5cf6'];

    return (
        <div className="chart-container mt-4 sm:mt-6 overflow-hidden min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
                <div className="min-w-0">
                    <h3 className="text-base sm:text-xl font-bold text-foreground flex items-center gap-2 truncate">
                        <FiActivity className="text-[color:var(--primary)] flex-shrink-0" />
                        <span className="truncate">Performance Comparison</span>
                    </h3>
                    <p className="text-xs sm:text-sm text-muted mt-1">Normalized % change from start</p>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {(['1W', '1M', '3M', '6M'] as const).map(tf => (
                        <button
                            key={tf}
                            onClick={() => setTimeframe(tf)}
                            className={`px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-all ${timeframe === tf
                                ? 'bg-[var(--primary)] text-[color:var(--on-solid)] shadow-lg'
                                : 'bg-surface text-foreground hover:bg-surface-elevated'
                                }`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>
            </div>
            <div className="h-56 sm:h-64 md:h-80 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data}>
                        <defs>
                            {Object.keys(chartData).map((symbol, idx) => (
                                <linearGradient key={symbol} id={`gradient-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={colors[idx % colors.length]} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={colors[idx % colors.length]} stopOpacity={0} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            stroke="var(--muted-foreground)"
                            fontSize={11}
                        />
                        <YAxis
                            tickFormatter={(value) => `${value.toFixed(0)}%`}
                            stroke="var(--muted-foreground)"
                            fontSize={11}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'var(--surface-elevated)',
                                border: '1px solid var(--border)',
                                borderRadius: '12px',
                                color: 'var(--foreground)',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value: number) => `${value.toFixed(2)}%`}
                            labelFormatter={(label) => new Date(label).toLocaleDateString()}
                        />
                        <Legend
                            wrapperStyle={{
                                paddingTop: '20px',
                                fontSize: '12px',
                                fontWeight: 600
                            }}
                        />
                        {Object.keys(chartData).map((symbol, idx) => (
                            <Line
                                key={symbol}
                                type="monotone"
                                dataKey={symbol}
                                stroke={colors[idx % colors.length]}
                                strokeWidth={3}
                                dot={false}
                                activeDot={{ r: 6 }}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// Enhanced Price Chart
const PriceChart: React.FC<{ data: ChartDataPoint[]; symbol: string }> = ({ data, symbol }) => {
    const [timeframe, setTimeframe] = useState<'1W' | '1M' | '3M' | '6M'>('1M');

    const filterDataByTimeframe = (data: ChartDataPoint[]) => {
        const now = Date.now();
        const days = { '1W': 7, '1M': 30, '3M': 90, '6M': 180 };
        const cutoff = now - days[timeframe] * 24 * 60 * 60 * 1000;
        return data.filter(d => d.timestamp * 1000 > cutoff);
    };

    const filteredData = filterDataByTimeframe(data);
    const minPrice = Math.min(...filteredData.map(d => d.low || d.close));
    const maxPrice = Math.max(...filteredData.map(d => d.high || d.close));
    const padding = (maxPrice - minPrice) * 0.1;

    const isPositive = filteredData.length >= 2 &&
        filteredData[filteredData.length - 1].close >= filteredData[0].close;

    const priceChange = filteredData.length >= 2
        ? ((filteredData[filteredData.length - 1].close - filteredData[0].close) / filteredData[0].close) * 100
        : 0;

    return (
        <div className="chart-container min-w-0 overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6">
                <div className="min-w-0">
                    <h4 className="text-base sm:text-lg font-bold text-foreground flex items-center gap-2 truncate">
                        <FiActivity className="text-[color:var(--primary)] flex-shrink-0" />
                        <span className="truncate">{symbol} Price</span>
                    </h4>
                    <p className={`text-xs sm:text-sm font-semibold mt-1 flex items-center gap-1 ${isPositive ? 'text-[color:var(--success)]' : 'text-[color:var(--danger)]'}`}>
                        {isPositive ? <FiTrendingUp /> : <FiTrendingDown />}
                        {isPositive ? '+' : ''}{priceChange.toFixed(2)}% {timeframe}
                    </p>
                </div>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                    {(['1W', '1M', '3M', '6M'] as const).map(tf => (
                        <button
                            key={tf}
                            onClick={() => setTimeframe(tf)}
                            className={`px-2.5 py-1 sm:px-3 sm:py-1.5 text-xs font-semibold rounded-lg transition-all ${timeframe === tf
                                ? 'bg-[var(--primary)] text-[color:var(--on-solid)] shadow-lg'
                                : 'bg-surface-elevated text-foreground hover:bg-surface'
                                }`}
                        >
                            {tf}
                        </button>
                    ))}
                </div>
            </div>
            <div className="h-52 sm:h-64 md:h-72 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={filteredData}>
                        <defs>
                            <linearGradient id={`gradient-${symbol}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity={0.4} />
                                <stop offset="95%" stopColor={isPositive ? '#10b981' : '#ef4444'} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            stroke="var(--muted-foreground)"
                            fontSize={10}
                        />
                        <YAxis
                            domain={[minPrice - padding, maxPrice + padding]}
                            tickFormatter={(value) => `$${value.toFixed(0)}`}
                            stroke="var(--muted-foreground)"
                            fontSize={10}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'var(--surface-elevated)',
                                border: '1px solid var(--border)',
                                borderRadius: '12px',
                                color: 'var(--foreground)',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            formatter={(value: number) => [`$${value.toFixed(2)}`, 'Price']}
                            labelFormatter={(label) => new Date(label).toLocaleDateString()}
                        />
                        <Area
                            type="monotone"
                            dataKey="close"
                            stroke={isPositive ? '#10b981' : '#ef4444'}
                            strokeWidth={3}
                            fill={`url(#gradient-${symbol})`}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// Enhanced Volume Chart
const VolumeChart: React.FC<{ data: ChartDataPoint[]; symbol: string }> = ({ data, symbol }) => {
    const recentData = data.slice(-30);
    const avgVolume = recentData.reduce((sum, d) => sum + d.volume, 0) / recentData.length;

    return (
        <div className="chart-container min-w-0 overflow-hidden">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="min-w-0">
                    <h4 className="text-sm sm:text-base font-bold text-foreground flex items-center gap-2 truncate">
                        <FiBarChart2 className="text-[color:var(--accent)] flex-shrink-0" />
                        Volume Trends
                    </h4>
                    <p className="text-xs text-muted mt-1">Avg: {(avgVolume / 1e6).toFixed(2)}M</p>
                </div>
            </div>
            <div className="h-32 sm:h-40 min-w-0">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={recentData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.2} />
                        <XAxis dataKey="date" hide />
                        <YAxis
                            tickFormatter={(value) => `${(value / 1e6).toFixed(0)}M`}
                            stroke="var(--muted-foreground)"
                            fontSize={10}
                        />
                        <Tooltip
                            contentStyle={{
                                backgroundColor: 'var(--surface-elevated)',
                                border: '1px solid var(--border)',
                                borderRadius: '12px',
                                color: 'var(--foreground)'
                            }}
                            formatter={(value: number) => [value.toLocaleString(), 'Volume']}
                        />
                        <Bar
                            dataKey="volume"
                            fill="url(#volumeGradient)"
                            radius={[4, 4, 0, 0]}
                        />
                        <defs>
                            <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.8} />
                                <stop offset="100%" stopColor="#6366f1" stopOpacity={0.3} />
                            </linearGradient>
                        </defs>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

// Enhanced Stock Card
const StockCard: React.FC<{ stock: StockData; isComparison?: boolean }> = ({ stock, isComparison = false }) => {
    const [expanded, setExpanded] = useState(false);
    const isPositive = (stock.quote?.change || 0) >= 0;

    return (
        <div className="gradient-border overflow-hidden animate-fadeIn min-w-0">
            <div className="bg-surface-elevated rounded-xl sm:rounded-2xl">
                {/* Header with Gradient */}
                <div className="relative overflow-hidden">
                    <div className={`absolute inset-0 ${isPositive
                        ? 'bg-gradient-to-br from-green-500/10 to-transparent'
                        : 'bg-gradient-to-br from-red-500/10 to-transparent'
                        }`}></div>
                    <div className="relative p-4 sm:p-6">
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="space-y-1.5 sm:space-y-2 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                    <h3 className="text-xl sm:text-2xl font-black text-foreground tracking-tight">{stock.symbol}</h3>
                                    <span className={`px-3 py-1 text-xs font-bold rounded-full ${stock.isForex
                                        ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
                                        : stock.technicalIndicators?.trend === 'Bullish'
                                            ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                                            : stock.technicalIndicators?.trend === 'Bearish'
                                                ? 'bg-gradient-to-r from-red-500 to-rose-500 text-white'
                                                : 'bg-surface text-muted'
                                        }`}>
                                        {stock.isForex ? 'FX' : (stock.technicalIndicators?.trend || 'Neutral')}
                                    </span>
                                </div>
                                {(stock.profile?.name || stock.profile?.sector || stock.quote?.exchange) && (
                                    <>
                                        {stock.profile?.name && (
                                            <p className="text-sm font-medium text-muted">{stock.profile.name}</p>
                                        )}
                                        {(stock.profile?.sector || stock.quote?.exchange) && (
                                            <div className="flex items-center gap-2 text-xs">
                                                <span className="px-2 py-1 bg-surface rounded-md text-muted font-medium">
                                                    {stock.profile?.sector || '—'}
                                                </span>
                                                {stock.profile?.sector && stock.quote?.exchange && <span className="text-muted">•</span>}
                                                <span className="text-muted">{stock.quote?.exchange || '—'}</span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="text-left sm:text-right space-y-1">
                                <p className="text-2xl sm:text-3xl font-black text-foreground">
                                    {stock.isForex ? (stock.quote?.price != null ? stock.quote.price.toFixed(4) : '—') : `$${formatNumber(stock.quote?.price)}`}
                                </p>
                                <p className={`text-sm sm:text-base font-bold flex items-center gap-2 ${isPositive ? 'text-green-500' : 'text-red-500'
                                    }`}>
                                    {isPositive ? <FiTrendingUp className="w-5 h-5" /> : <FiTrendingDown className="w-5 h-5" />}
                                    <span>{isPositive ? '+' : ''}{stock.isForex ? (stock.quote?.change != null ? stock.quote.change.toFixed(4) : '—') : formatNumber(stock.quote?.change)}</span>
                                    <span className="text-sm">({formatNumber(stock.quote?.changePercent)}%)</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Quick Metrics Grid - only show cards when value is fetched */}
                {(() => {
                    const isForex = stock.isForex === true;
                    const dayRangeVal = (stock.quote?.dayHigh != null && stock.quote?.dayLow != null)
                        ? isForex
                            ? `${stock.quote.dayLow.toFixed(4)} - ${stock.quote.dayHigh.toFixed(4)}`
                            : `${formatNumber(stock.quote.dayLow, 0)} - ${formatNumber(stock.quote.dayHigh, 0)}`
                        : null;
                    const volumeVal = stock.quote?.volume != null ? `${((stock.quote.volume) / 1e6).toFixed(1)}M` : null;
                    const marketCapVal = !isForex && stock.quote?.marketCap != null ? formatLargeNumber(stock.quote.marketCap) : null;
                    const peVal = !isForex && stock.financialStats?.peRatio != null ? formatNumber(stock.financialStats.peRatio) : null;
                    const metrics = [
                        dayRangeVal !== null && { label: isForex ? 'Day Range (rate)' : 'Day Range', value: isForex ? dayRangeVal : `$${dayRangeVal}` },
                        volumeVal !== null && { label: 'Volume', value: volumeVal },
                        marketCapVal !== null && marketCapVal !== '—' && { label: 'Market Cap', value: marketCapVal },
                        peVal !== null && peVal !== '—' && { label: 'P/E Ratio', value: peVal },
                    ].filter(Boolean) as { label: string; value: string }[];
                    if (metrics.length === 0) return null;
                    return (
                        <div className={`grid gap-2 sm:gap-3 p-4 sm:p-6 bg-gradient-to-b from-surface to-surface-elevated ${metrics.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
                            {metrics.map((m) => (
                                <div key={m.label} className="metric-card">
                                    <p className="text-xs font-semibold text-muted mb-1">{m.label}</p>
                                    <p className="text-sm font-bold text-foreground">{m.value}</p>
                                </div>
                            ))}
                        </div>
                    );
                })()}

                {/* Expandable Section */}
                {/* <button
                    onClick={() => setExpanded(!expanded)}
                    className="w-full px-6 py-3 flex items-center justify-center gap-2 text-sm font-semibold text-primary hover:bg-surface/50 transition-all border-t border-border"
                >
                    {expanded ? 'Hide Advanced Metrics' : 'Show Advanced Metrics'}
                    {expanded ? <FiChevronUp className="w-4 h-4" /> : <FiChevronDown className="w-4 h-4" />}
                </button> */}

                {expanded && (
                    <div className="p-6 space-y-6 bg-surface border-t border-border animate-slideIn">
                        {/* Technical Indicators */}
                        {stock.technicalIndicators && (
                            <div>
                                <h4 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                                    <div className="w-1 h-4 bg-gradient-to-b from-primary to-accent rounded-full"></div>
                                    Technical Indicators
                                </h4>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-primary transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">RSI (14)</p>
                                        <p className={`text-xl font-black ${stock.technicalIndicators.rsiSignal === 'Overbought'
                                            ? 'text-red-500'
                                            : stock.technicalIndicators.rsiSignal === 'Oversold'
                                                ? 'text-green-500'
                                                : 'text-foreground'
                                            }`}>
                                            {formatNumber(stock.technicalIndicators.rsi, 0)}
                                        </p>
                                        <p className="text-xs text-muted mt-1">{stock.technicalIndicators.rsiSignal}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-primary transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">SMA 50</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.technicalIndicators.sma50, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-primary transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">SMA 200</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.technicalIndicators.sma200, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-green-500/20 hover:border-green-500 transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">Support</p>
                                        <p className="text-xl font-black text-green-500">${formatNumber(stock.technicalIndicators.support, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-red-500/20 hover:border-red-500 transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">Resistance</p>
                                        <p className="text-xl font-black text-red-500">${formatNumber(stock.technicalIndicators.resistance, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-primary transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">MACD</p>
                                        <p className="text-xl font-black text-foreground">{formatNumber(stock.technicalIndicators.macd)}</p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Financial Stats */}
                        {stock.financialStats && (
                            <div>
                                <h4 className="text-sm font-bold text-foreground mb-4 flex items-center gap-2">
                                    <div className="w-1 h-4 bg-gradient-to-b from-accent to-primary rounded-full"></div>
                                    Fundamental Analysis
                                </h4>
                                <div className="grid grid-cols-3 gap-3">
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-accent transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">EPS</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.financialStats.eps)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-accent transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">52W High</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.financialStats.fiftyTwoWeekHigh, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-accent transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">52W Low</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.financialStats.fiftyTwoWeekLow, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-accent transition-all">
                                        <p className="text-xs font-semibold text-muted mb-2">Target Price</p>
                                        <p className="text-xl font-black text-foreground">${formatNumber(stock.financialStats.targetMeanPrice, 0)}</p>
                                    </div>
                                    <div className="bg-surface-elevated rounded-xl p-4 border border-border hover:border-accent transition-all col-span-2">
                                        <p className="text-xs font-semibold text-muted mb-2">Analyst Rating</p>
                                        <p className="text-xl font-black text-foreground capitalize">
                                            {safeDisplay(stock.financialStats.recommendationKey)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

// Wrapper: icon click par graph fullscreen (fixed inset-0)
const ChartWithExpand: React.FC<{ children: React.ReactNode; expandContent: React.ReactNode; title?: string }> = ({ children, expandContent, title }) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const fullscreenContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isFullscreen) return;
        const el = fullscreenContentRef.current;
        if (el) {
            el.scrollTop = 0;
            requestAnimationFrame(() => { el.scrollTop = 0; });
        }
    }, [isFullscreen]);

    return (
        <>
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setIsFullscreen(true)}
                    className="absolute top-1 right-2 z-10 p-1 rounded-lg bg-surface-elevated/90 hover:bg-surface border border-border text-muted hover:text-foreground transition-all shadow-md"
                    aria-label="Fullscreen chart"
                >
                    <FiMaximize2 className="w-5 h-5" />
                </button>
                {children}
            </div>
            {isFullscreen && (
            <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50"
            onClick={() => setIsFullscreen(false)}
          >
            <div
              className="relative w-[90%] max-w-4xl max-h-[90vh] bg-background rounded-xl shadow-2xl border border-border flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <div className="absolute top-3 right-3 z-10">
                <button
                  type="button"
                  onClick={() => setIsFullscreen(false)}
                  className="p-2 rounded-lg hover:bg-surface text-muted hover:text-foreground"
                  aria-label="Close"
                >
                  <FiMinimize2 className="w-5 h-5" />
                </button>
              </div>
          
              {/* Content */}
              <div
                ref={fullscreenContentRef}
                className="flex-1 overflow-auto p-6 pt-12"
              >
                {expandContent}
              </div>
            </div>
          </div>
            )}
        </>
    );
};

// TradingView chart embed (iframe) - shown next to stock card (compact) or standalone
const TradingViewChart: React.FC<{ symbol: string; title?: string; compact?: boolean }> = ({ symbol, title, compact }) => {
    // TradingView uses exchange:symbol. Forex uses FX:EURUSD (no =X). Yahoo forex = EURUSD=X, GC=F for gold.
    let tvSymbol: string;
    if (symbol.includes(':')) {
        tvSymbol = symbol;
    } else if (symbol.endsWith('=X')) {
        tvSymbol = `FX:${symbol.replace('=X', '')}`;
    } else if (symbol === 'GC=F') {
        tvSymbol = 'COMEX:GC';
    } else {
        tvSymbol = `NASDAQ:${symbol}`;
    }
    const embedUrl = `https://www.tradingview.com/widgetembed/?frameElementId=tv_${encodeURIComponent(symbol)}&symbol=${encodeURIComponent(tvSymbol)}&interval=D&hidesidebar=1&symboledit=0&saveimage=0&toolbarbg=1e222d&studies=%5B%5D&theme=dark&style=1&timezone=America%2FNew_York&withdateranges=1&showpopupbutton=0&locale=en`;
    const content = (
        <>
            {title && (
                <p className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-muted border-b border-border truncate">{title}</p>
            )}
            <iframe
                title={`TradingView chart ${symbol}`}
                src={embedUrl}
                className={`w-full min-w-0 ${compact ? 'h-[260px] sm:h-[320px]' : 'h-[320px] sm:h-[400px]'}`}
                frameBorder={0}
                allowFullScreen
            />
        </>
    );
    if (compact) return <div className="flex flex-col min-h-0">{content}</div>;
    return (
        <div className="mt-4 rounded-xl border border-border overflow-hidden bg-surface-elevated">
            {content}
        </div>
    );
};

// Format price for display: forex = rate (4 decimals), stocks = $ and 2 decimals
const formatPriceDisplay = (value: number | undefined, isForex?: boolean): string => {
    if (value == null) return '—';
    return isForex ? value.toFixed(4) : `$${formatNumber(value)}`;
};

// Stock details & comparison table (price and key metrics in a clean table)
const StockDetailsTable: React.FC<{ stocks: StockData[] }> = ({ stocks }) => {
    if (!stocks || stocks.length === 0) return null;

    const rows = [
        { key: 'Price', get: (s: StockData) => s.quote?.price != null ? formatPriceDisplay(s.quote.price, s.isForex) : '—' },
        { key: 'Change', get: (s: StockData) => s.quote?.change != null ? `${(s.quote.change >= 0 ? '+' : '')}${(s.isForex ? s.quote.change.toFixed(4) : formatNumber(s.quote.change))}` : '—', positive: (s: StockData) => (s.quote?.change ?? 0) >= 0 },
        { key: 'Change %', get: (s: StockData) => s.quote?.changePercent != null ? `${(s.quote.changePercent >= 0 ? '+' : '')}${formatNumber(s.quote.changePercent)}%` : '—', positive: (s: StockData) => (s.quote?.changePercent ?? 0) >= 0 },
        { key: 'Day High', get: (s: StockData) => s.quote?.dayHigh != null ? formatPriceDisplay(s.quote.dayHigh, s.isForex) : '—' },
        { key: 'Day Low', get: (s: StockData) => s.quote?.dayLow != null ? formatPriceDisplay(s.quote.dayLow, s.isForex) : '—' },
        { key: 'Volume', get: (s: StockData) => s.quote?.volume != null ? `${((s.quote.volume) / 1e6).toFixed(2)}M` : '—' },
        { key: 'Market Cap', get: (s: StockData) => s.isForex ? '—' : formatLargeNumber(s.quote?.marketCap) },
        { key: 'P/E Ratio', get: (s: StockData) => s.isForex ? '—' : formatNumber(s.financialStats?.peRatio) },
        { key: '52W High', get: (s: StockData) => s.financialStats?.fiftyTwoWeekHigh != null && !s.isForex ? `$${formatNumber(s.financialStats.fiftyTwoWeekHigh, 0)}` : '—' },
        { key: '52W Low', get: (s: StockData) => s.financialStats?.fiftyTwoWeekLow != null && !s.isForex ? `$${formatNumber(s.financialStats.fiftyTwoWeekLow, 0)}` : '—' },
        { key: 'Sector', get: (s: StockData) => s.isForex ? '—' : safeDisplay(s.profile?.sector) },
        { key: 'Trend', get: (s: StockData) => safeDisplay(s.technicalIndicators?.trend) },
    ];

    // Only show rows where at least one stock has fetched data (not —)
    const visibleRows = rows.filter((row) =>
        stocks.some((s) => {
            const v = row.get(s);
            const str = v != null ? String(v).trim() : '';
            return str !== '' && str !== '—';
        })
    );

    if (visibleRows.length === 0) return null;

    return (
        <div className="mt-3 sm:mt-4 overflow-x-auto overflow-hidden rounded-lg sm:rounded-xl border border-border shadow-lg animate-fadeIn bg-surface-elevated min-w-0 -mx-1 sm:mx-0">
            <div className="overflow-x-auto min-w-0">
                <table className="min-w-full border-collapse text-xs sm:text-sm">
                    <thead>
                        <tr className="bg-neutral-900/80 border-b border-border">
                            <th className="px-2 py-1.5 sm:px-3 sm:py-2 text-left text-[10px] sm:text-xs font-bold text-muted uppercase tracking-wider sticky left-0 bg-neutral-900/95 z-10 border-r border-border">
                                Metric
                            </th>
                            {stocks.map((s, i) => (
                                <th key={i} className="px-2 py-1.5 sm:px-3 sm:py-2 text-center text-xs sm:text-sm font-bold text-foreground whitespace-nowrap">
                                    <span className="text-primary">{s.symbol}</span>
                                    {s.profile?.name && (
                                        <p className="text-[10px] sm:text-xs font-normal text-muted mt-0.5 truncate max-w-[100px] sm:max-w-[140px] mx-auto">{s.profile.name}</p>
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {visibleRows.map((row, rowIdx) => (
                            <tr
                                key={row.key}
                                className={rowIdx % 2 === 0 ? 'bg-surface-elevated' : 'bg-surface/50'}
                            >
                                <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-semibold text-muted border-r border-border sticky left-0 bg-inherit whitespace-nowrap">
                                    {row.key}
                                </td>
                                {stocks.map((s, i) => {
                                    const raw = row.get(s);
                                    const val = safeDisplay(raw);
                                    const isPositive = 'positive' in row && row.positive && row.positive(s);
                                    const isNegative = 'positive' in row && row.positive && !row.positive(s);
                                    return (
                                        <td
                                            key={i}
                                            className={`px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-center font-medium whitespace-nowrap ${isPositive ? 'text-[color:var(--success)]' : isNegative ? 'text-[color:var(--danger)]' : 'text-foreground'
                                                }`}
                                        >
                                            {val}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Message Content Component
const MessageContent: React.FC<{ content: string; isStreaming?: boolean }> = ({ content, isStreaming = false }) => {
    const [displayedContent, setDisplayedContent] = React.useState('');
    const [currentIndex, setCurrentIndex] = React.useState(0);

    // Ultra-fast streaming effect: show 5-8 words at a time
    React.useEffect(() => {
        if (!isStreaming) {
            setDisplayedContent(content);
            return;
        }

        const words = content.split(/(\s+)/); // Split by whitespace but keep the spaces

        if (currentIndex >= words.length) {
            setDisplayedContent(content);
            return;
        }

        const timer = setTimeout(() => {
            // Add 8-12 words at a time for ultra-fast streaming
            const wordsToAdd = Math.floor(Math.random() * 5) + 8; // 8 to 12 words
            const nextIndex = Math.min(currentIndex + wordsToAdd * 2, words.length); // *2 because spaces are separate elements

            setDisplayedContent(words.slice(0, nextIndex).join(''));
            setCurrentIndex(nextIndex);
        }, 15); // Ultra-fast: 15ms interval for smoother streaming

        return () => clearTimeout(timer);
    }, [content, currentIndex, isStreaming]);

    const parseMarkdownTable = (text: string): { tables: Array<{ start: number; end: number; rows: string[][] }>, text: string } => {
        const lines = text.split('\n');
        const tables: Array<{ start: number; end: number; rows: string[][] }> = [];
        let currentTable: { start: number; rows: string[][] } | null = null;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line === '') continue;

            if (line.includes('|') && !line.startsWith('```')) {
                if (line.match(/^[\|\s\-:]+$/) && line.includes('-')) continue;

                const cells = line.split('|').map((cell: string) => cell.trim()).filter((cell: string) => cell.length > 0);

                if (cells.length > 0) {
                    if (!currentTable) currentTable = { start: i, rows: [] };
                    currentTable.rows.push(cells);
                }
            } else {
                if (currentTable && currentTable.rows.length > 1) {
                    tables.push({ ...currentTable, end: i - 1 });
                }
                currentTable = null;
            }
        }

        if (currentTable && currentTable.rows.length > 1) {
            tables.push({ ...currentTable, end: lines.length - 1 });
        }

        return { tables, text };
    };

    const formatText = (text: string) => {
        return text
            .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-foreground">$1</strong>')
            .replace(/\*(.*?)\*/g, '<em class="italic">$1</em>')
            .replace(/`(.*?)`/g, '<code class="bg-primary/10 text-primary px-2 py-0.5 rounded text-sm font-mono">$1</code>');
    };

    const renderTable = (table: { start: number; end: number; rows: string[][] }, tableKey: string) => {
        const headerRow = table.rows[0] || [];
        const bodyRows = table.rows.slice(1);
        if (headerRow.length === 0) return null;

        return (
            <div key={tableKey} className="my-3 overflow-x-auto overflow-hidden rounded-lg sm:rounded-xl border border-border shadow-lg animate-fadeIn min-w-0 -mx-0.5 sm:mx-0">
                <table className="min-w-full border-collapse text-xs sm:text-sm">
                    <thead className="bg-gradient-to-r from-primary/10 to-accent/10">
                        <tr>
                            {headerRow.map((cell: string, cellIdx: number) => (
                                <th
                                    key={cellIdx}
                                    className="px-3 py-2 sm:px-6 sm:py-4 text-left text-xs sm:text-sm font-black text-foreground border-b-2 border-primary/20 whitespace-nowrap"
                                    dangerouslySetInnerHTML={{ __html: formatText(cell) }}
                                />
                            ))}
                        </tr>
                    </thead>
                    {bodyRows.length > 0 && (
                        <tbody className="bg-surface-elevated">
                            {bodyRows.map((row: string[], rowIdx: number) => (
                                <tr
                                    key={rowIdx}
                                    className={`${rowIdx % 2 === 0 ? 'bg-surface-elevated' : 'bg-surface'
                                        } hover:bg-primary/5 transition-colors`}
                                >
                                    {row.map((cell: string, cellIdx: number) => {
                                        const isWinner = cell.toLowerCase().includes('winner');
                                        const isPositive = cell.includes('+') || cell.includes('✅') || cell.includes('Above') || cell.includes('Bullish') || cell.includes('🟢');
                                        const isNegative = cell.includes('Below') || cell.includes('Bearish') || cell.includes('🔴') || cell.includes('-');

                                        return (
                                            <td
                                                key={cellIdx}
                                                className={`px-3 py-2 sm:px-6 sm:py-4 text-xs sm:text-sm border-b border-border whitespace-nowrap ${isWinner ? 'font-black text-primary text-base sm:text-lg' :
                                                    isPositive ? 'font-bold text-green-500' :
                                                        isNegative ? 'font-bold text-red-500' :
                                                            'text-foreground'
                                                    }`}
                                                dangerouslySetInnerHTML={{ __html: formatText(cell) }}
                                            />
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    )}
                </table>
            </div>
        );
    };

    const formatContent = (raw: string) => {
        let text = raw;

        // Strip disclaimer blocks
        text = text.replace(/(\*\*)?⚠️\s*Important Disclaimers[\s\S]*?(?=^##? |\n##? |$)/gim, '');
        text = text.replace(/(\*\*)?⚠️\s*Important Notes[\s\S]*?(?=^##? |\n##? |$)/gim, '');
        text = text.replace(/\n{3,}/g, '\n\n');

        // Emoji substitutions
        text = text
            .replace(/🟡/g, '⚠️')
            .replace(/💡/g, '✨');

        const { tables } = parseMarkdownTable(text);
        const lineToTable = new Map<number, { table: { start: number; end: number; rows: string[][] }, isStart: boolean }>();
        tables.forEach((table) => {
            for (let i = table.start; i <= table.end; i++) {
                lineToTable.set(i, { table, isStart: i === table.start });
            }
        });

        const lines = text.split('\n');
        let inCodeBlock = false;
        let tableIndex = 0;
        const result: React.ReactNode[] = [];

        for (let index = 0; index < lines.length; index++) {
            const line = lines[index];
            const tableInfo = lineToTable.get(index);

            if (tableInfo) {
                if (tableInfo.isStart) {
                    const tableElement = renderTable(tableInfo.table, `table-${tableIndex++}`);
                    if (tableElement) result.push(tableElement);
                }
                continue;
            }

            if (line.startsWith('```')) {
                inCodeBlock = !inCodeBlock;
                continue;
            }

            if (inCodeBlock) {
                result.push(<code key={index} className="block bg-surface-elevated px-4 py-2 text-sm font-mono rounded-lg">{line}</code>);
                continue;
            }

            if (line.startsWith('### ')) {
                result.push(<h3 key={index} className="text-xl font-black mt-3 mb-1.5 text-foreground" dangerouslySetInnerHTML={{ __html: formatText(line.replace('### ', '')) }} />);
                continue;
            }
            if (line.startsWith('## ')) {
                result.push(<h2 key={index} className="text-2xl font-black mt-3 mb-1.5 text-foreground" dangerouslySetInnerHTML={{ __html: formatText(line.replace('## ', '')) }} />);
                continue;
            }
            if (line.startsWith('# ')) {
                result.push(<h1 key={index} className="text-3xl font-bold mt-3 mb-2 title-page" dangerouslySetInnerHTML={{ __html: formatText(line.replace('# ', '')) }} />);
                continue;
            }

            let formattedLine = formatText(line);

            if (line.startsWith('- ') || line.startsWith('• ')) {
                result.push(<li key={index} className="ml-5 mb-1 text-foreground list-disc" dangerouslySetInnerHTML={{ __html: formattedLine.substring(2) }} />);
                continue;
            }

            const numberedMatch = line.match(/^(\d+)\.\s/);
            if (numberedMatch) {
                result.push(<li key={index} className="ml-5 mb-1 text-foreground list-decimal" dangerouslySetInnerHTML={{ __html: formattedLine.replace(/^\d+\.\s/, '') }} />);
                continue;
            }

            if (line.trim() === '---' || line.trim() === '***') {
                result.push(<hr key={index} className="my-2 border-border opacity-50" />);
                continue;
            }

            if (line.trim() === '') {
                result.push(<br key={index} />);
                continue;
            }

            result.push(<p key={index} className="mb-1.5 text-foreground leading-relaxed" dangerouslySetInnerHTML={{ __html: formattedLine }} />);
        }

        return result;
    };

    return (
        <div className="prose prose-sm dark:prose-invert max-w-none min-w-0 overflow-hidden break-words">
            {formatContent(displayedContent)}
            {isStreaming && currentIndex < content.split(/(\s+)/).length && (
                <span className="inline-block w-1 h-4 ml-1 bg-primary animate-pulse" />
            )}
        </div>
    );
};

// Streaming cursor component
const StreamingCursor: React.FC = () => {
    return (
        <span className="inline-block w-1 h-1 rounded-full bg-primary ml-1 animate-pulse align-middle" />
    );
};

// ========================================
// MAIN COMPONENT
// ========================================

const StockAnalysisChatbot: React.FC = () => {
    const { user } = useAuthStore();
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [currentStatus, setCurrentStatus] = useState<'fetching_price' | 'analyzing_charts' | null>(null);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const streamingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isLoadingMoreRef = useRef(false);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Scroll to bottom function - always scrolls (no conditions)
    const scrollToBottom = useCallback((behavior: 'smooth' | 'auto' = 'smooth') => {
        const container = messagesContainerRef.current;
        if (container) {
            container.scrollTo({
                top: container.scrollHeight,
                behavior
            });
            setShowScrollToBottom(false);
        }
    }, []);

    // Load conversations on mount
    useEffect(() => {
        if (user) {
            loadConversations();
        }
    }, [user]);

    useEffect(() => {
        // Don't auto-scroll if we're loading older messages (infinite scroll)
        if (isLoadingMoreMessages) {
            return;
        }

        const container = messagesContainerRef.current;
        if (container && messages.length > 0) {
            // Small delay to ensure DOM is updated, then check scroll position
            setTimeout(() => {
                if (!container) return;

                // Check if user is near bottom before auto-scrolling
                const threshold = 200;
                const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                const isNearBottom = scrollBottom < threshold;

                // Only auto-scroll if user is near bottom (not reading older messages)
                if (isNearBottom) {
                    container.scrollTo({
                        top: container.scrollHeight,
                        behavior: 'smooth'
                    });
                    setShowScrollToBottom(false);
                }
            }, 100);
        }
    }, [messages.length, isLoadingMoreMessages]);

    // Auto-scroll during streaming - always scroll to bottom
    useEffect(() => {
        const hasStreaming = messages.some(m => m.status === 'streaming');
        if (hasStreaming) {
            // Scroll immediately when streaming starts
            scrollToBottom('auto');
            
            // Continue scrolling during streaming
            const scrollInterval = setInterval(() => {
                scrollToBottom('smooth');
            }, 50); // Scroll every 50ms during streaming for smooth following

            return () => clearInterval(scrollInterval);
        }
    }, [messages, scrollToBottom]);

    useEffect(() => {
        return () => {
            if (streamingTimeoutRef.current) {
                clearTimeout(streamingTimeoutRef.current);
            }
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Listen for openChatHistory event from Sidebar
    useEffect(() => {
        const handleOpenHistory = () => setShowHistory(true);
        window.addEventListener('openChatHistory', handleOpenHistory);
        return () => window.removeEventListener('openChatHistory', handleOpenHistory);
    }, []);

    useEffect(() => {
        const container = messagesContainerRef.current;
        if (container && messages.length > 0) {
            const checkScrollPosition = () => {
                const threshold = 150;
                const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
                const atBottom = scrollBottom < threshold;
                setShowScrollToBottom(!atBottom);
            };

            // Check immediately
            checkScrollPosition();

            // Also check after a short delay to account for layout
            const timeout = setTimeout(checkScrollPosition, 300);
            return () => clearTimeout(timeout);
        }
    }, [messages.length]);

    const loadConversations = async () => {
        try {
            setIsLoadingHistory(true);
            const response = await apiClient.get('/ai-chat/conversations');
            setConversations(response.data.conversations || []);
        } catch (error: any) {
            console.error('Error loading conversations:', error);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const loadMessages = useCallback(async (conversationId: string, before?: Date) => {
        if (isLoadingMoreRef.current) {
            console.log('Already loading messages, skipping duplicate call');
            return [];
        }

        try {
            isLoadingMoreRef.current = true;
            setIsLoadingMoreMessages(true);

            const params: any = { limit: 20 }; // Increased from 1 to 20 for better UX
            if (before) {
                params.before = before.toISOString();
                console.log('Loading messages before:', before.toISOString());
            }

            const response = await apiClient.get(
                `/ai-chat/conversations/${conversationId}/messages`,
                { params }
            );

            console.log('Loaded messages:', response.data.messages.length, 'hasMore:', response.data.hasMore);

            const loadedMessages: Message[] = response.data.messages.map((msg: any) => ({
                id: msg.id,
                role: msg.role,
                content: msg.content,
                displayedContent: msg.content, // Already complete, no streaming needed
                timestamp: new Date(msg.timestamp),
                symbols: msg.symbols,
                stockData: msg.stockData,
                chartData: msg.chartData,
                isLoading: false
            }));

            if (before) {
                // Prepend older messages and preserve scroll position
                const container = messagesContainerRef.current;
                if (container) {
                    const previousScrollHeight = container.scrollHeight;
                    const previousScrollTop = container.scrollTop;

                    setMessages(prev => [...loadedMessages, ...prev]);

                    // Restore scroll position after DOM update to prevent jump
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (container) {
                                const newScrollHeight = container.scrollHeight;
                                const scrollDifference = newScrollHeight - previousScrollHeight;
                                container.scrollTop = previousScrollTop + scrollDifference;
                                console.log('Scroll adjusted:', {
                                    previousScrollHeight,
                                    newScrollHeight,
                                    scrollDifference,
                                    newScrollTop: container.scrollTop
                                });
                            }
                        });
                    });
                } else {
                    setMessages(prev => [...loadedMessages, ...prev]);
                }
            } else {
                // Replace messages (new conversation)
                setMessages(loadedMessages);
            }

            setHasMoreMessages(response.data.hasMore);
            console.log('Updated hasMoreMessages:', response.data.hasMore);
            return loadedMessages;
        } catch (error: any) {
            console.error('Error loading messages:', error);
            showToast('error', 'Failed to load messages');
            return [];
        } finally {
            setIsLoadingMoreMessages(false);
            isLoadingMoreRef.current = false;
        }
    }, []);

    const handleScroll = useCallback(() => {
        const container = messagesContainerRef.current;
        if (!container) return;

        // Show "scroll to bottom" when user has scrolled up (not near bottom)
        const threshold = 150;
        const scrollBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        const atBottom = scrollBottom < threshold;
        setShowScrollToBottom(!atBottom);

        // Clear any pending scroll timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Debounce infinite scroll check
        scrollTimeoutRef.current = setTimeout(() => {
            // Infinite scroll: load more messages when near top
            if (isLoadingMoreRef.current) {
                console.log('Already loading more messages, skipping...');
                return;
            }

            if (!hasMoreMessages) {
                console.log('No more messages to load');
                return;
            }

            if (!currentConversationId) {
                console.log('No conversation selected');
                return;
            }

            if (messages.length === 0) {
                console.log('No messages yet');
                return;
            }

            const container = messagesContainerRef.current;
            if (!container) return;

            // Check if scrolled near top - reduced threshold for better detection
            const scrollThreshold = 100; // Reduced from 300px to 100px
            const isNearTop = container.scrollTop <= scrollThreshold;

            console.log('Scroll position:', {
                scrollTop: container.scrollTop,
                scrollThreshold,
                isNearTop,
                hasMoreMessages,
                isLoadingMore: isLoadingMoreRef.current,
                messagesCount: messages.length
            });

            if (isNearTop) {
                const oldestMessage = messages[0];
                if (oldestMessage) {
                    console.log('Triggering load more messages, oldest timestamp:', oldestMessage.timestamp);
                    loadMessages(currentConversationId, oldestMessage.timestamp);
                }
            }
        }, 150); // Slightly increased debounce for stability
    }, [messages, hasMoreMessages, currentConversationId, loadMessages]);

    const handleConversationClick = async (conversationId: string) => {
        setCurrentConversationId(conversationId);
        setMessages([]);
        await loadMessages(conversationId);
        setShowHistory(false);

        // Scroll to bottom after messages are loaded
        setTimeout(() => {
            const container = messagesContainerRef.current;
            if (container) {
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'auto'
                });
                setShowScrollToBottom(false);
            }
        }, 100);
    };

    // Create new conversation
    const handleNewConversation = () => {
        setCurrentConversationId(null);
        setMessages([]);
        setShowHistory(false);
    };

    // Delete conversation
    const handleDeleteConversation = async (conversationId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        try {
            await apiClient.delete(`/ai-chat/conversations/${conversationId}`);
            setConversations(prev => prev.filter(c => c._id !== conversationId));
            if (currentConversationId === conversationId) {
                handleNewConversation();
            }
            showToast('success', 'Conversation deleted');
        } catch (error: any) {
            console.error('Error deleting conversation:', error);
            showToast('error', 'Failed to delete conversation');
        }
    };

    const streamMessage = useCallback((messageId: string, fullContent: string, symbols?: string[], stockData?: StockData[], chartData?: { [symbol: string]: ChartDataPoint[] }) => {
        // Clear any existing streaming timeout
        if (streamingTimeoutRef.current) {
            clearTimeout(streamingTimeoutRef.current);
        }

        const words = fullContent.split(/(\s+)/); // Split by words including spaces
        let currentWordIndex = 0;
        const wordsPerChunk = 8; // Show 8 words at a time for faster streaming

        const stream = () => {
            if (currentWordIndex < words.length) {
                const nextIndex = Math.min(currentWordIndex + wordsPerChunk * 2, words.length); // *2 for spaces
                const displayedContent = words.slice(0, nextIndex).join('');

                setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                        ? { ...msg, displayedContent, status: 'streaming' as const, isLoading: true }
                        : msg
                ));

                // Scroll to bottom during streaming
                scrollToBottom('smooth');

                currentWordIndex = nextIndex;
                streamingTimeoutRef.current = setTimeout(stream, 50); // 50ms between chunks
            } else {
                // Streaming complete
                setMessages(prev => prev.map(msg =>
                    msg.id === messageId
                        ? {
                            ...msg,
                            displayedContent: fullContent,
                            content: fullContent,
                            status: undefined,
                            isLoading: false,
                            symbols,
                            stockData,
                            chartData
                        }
                        : msg
                ));
                streamingTimeoutRef.current = null;
                
                // Final scroll when streaming completes
                setTimeout(() => scrollToBottom('smooth'), 100);
            }
        };

        stream();
    }, [scrollToBottom]);

    const handleSend = async (customQuery?: string) => {
        const query = customQuery || input;
        if (!query.trim() || isLoading) return;

        const messageId = Date.now().toString();
        const userMessage: Message = {
            id: messageId,
            role: 'user',
            content: query,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);
        
        // Scroll to bottom immediately when user sends message
        setTimeout(() => scrollToBottom('auto'), 50);

        const loadingId = (Date.now() + 1).toString();

        // Stage 1: Fetching price
        setCurrentStatus('fetching_price');
        setMessages(prev => [...prev, {
            id: loadingId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isLoading: true,
            status: 'fetching_price'
        }]);

        try {
            // Simulate price fetching delay (in real scenario, this happens on backend)
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Stage 2: Analyzing charts
            setCurrentStatus('analyzing_charts');
            setMessages(prev => prev.map(msg =>
                msg.id === loadingId
                    ? { ...msg, status: 'analyzing_charts' as const }
                    : msg
            ));

            const response = await apiClient.post(
                '/ai-chat/analyze',
                {
                    query: query,
                    conversationId: currentConversationId,
                    conversationHistory: messages.slice(-6).map(m => ({
                        role: m.role,
                        content: m.content
                    }))
                }
            );

            // Update conversation ID if it's a new conversation
            if (response.data.conversationId && !currentConversationId) {
                setCurrentConversationId(response.data.conversationId);
                await loadConversations();
            }

            // Stage 3: Stream the response
            setCurrentStatus(null);
            const assistantMessageId = (Date.now() + 2).toString();
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== loadingId);
                return [...filtered, {
                    id: assistantMessageId,
                    role: 'assistant',
                    content: response.data.analysis,
                    displayedContent: '',
                    timestamp: new Date(),
                    symbols: response.data.symbols,
                    stockData: response.data.stockData,
                    chartData: response.data.chartData,
                    relatedQuestions: response.data.relatedQuestions || [],
                    isLoading: true,
                    status: 'streaming'
                }];
            });

            // Start streaming the response
            streamMessage(
                assistantMessageId,
                response.data.analysis,
                response.data.symbols,
                response.data.stockData,
                response.data.chartData
            );

        } catch (error: any) {
            console.error('Chat error:', error);
            setCurrentStatus(null);
            setMessages(prev => {
                const filtered = prev.filter(m => m.id !== loadingId);
                return [...filtered, {
                    id: (Date.now() + 2).toString(),
                    role: 'assistant',
                    content: `⚠️ **System Error**

${error.response?.data?.error || error.message || 'Unable to process request. Please try again.'}

**Troubleshooting:**
- Verify your internet connection
- Ensure stock symbols are valid
- Try rephrasing your query`,
                    timestamp: new Date()
                }];
            });
            showToast('error', 'Analysis failed. Please retry.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const quickActions = [
        { icon: <FiTrendingUp />, label: 'AAPL Analysis', query: 'Deep dive analysis of Apple (AAPL) with technical indicators', gradient: 'from-blue-500 to-cyan-500' },
        { icon: <FiBarChart2 />, label: 'TSLA Metrics', query: 'Complete technical and fundamental breakdown of Tesla (TSLA)', gradient: 'from-purple-500 to-pink-500' },
        { icon: <FiTarget />, label: 'Compare Stocks', query: 'Compare NVDA vs AMD - which is the better investment?', gradient: 'from-orange-500 to-red-500' },
        { icon: <FiZap />, label: 'Market Pulse', query: 'What are the top tech stocks trending today?', gradient: 'from-green-500 to-emerald-500' },
        { icon: <FiGlobe />, label: 'Forex: EUR/USD', query: 'What is the current EUR/USD rate and short outlook?', gradient: 'from-amber-500 to-yellow-500' },
        { icon: <FiDollarSign />, label: 'Major forex pairs', query: 'Show me prices for major forex pairs: EUR/USD, GBP/USD, USD/JPY', gradient: 'from-teal-500 to-cyan-500' },
    ];

    // Same 4 quick-action items for starter (all 4) and above input when chat active (first 2 only)
    const quickActionItems = [
        { label: 'Analyze Apple (AAPL)', query: 'Analyze Apple (AAPL) stock with technical and fundamental metrics' },
        { label: 'Compare Tesla vs NVIDIA', query: 'Compare Tesla (TSLA) vs NVIDIA (NVDA) - which is the better investment?' },
        { label: 'Top tech stocks today', query: 'What are the top US tech stocks to watch today?' },
        { label: 'Amazon & Microsoft', query: 'Analyze Amazon (AMZN) and Microsoft (MSFT) and compare their performance' },
        { label: 'EUR/USD and GBP/USD', query: 'What are the current prices for EUR/USD and GBP/USD?' },
    ];

    const showStarter = messages.length === 0;
    const lastRelatedQuestions = [...messages].reverse().find(m => m.role === 'assistant' && !m.isLoading && m.relatedQuestions?.length)?.relatedQuestions ?? [];

    return (
        <>
            {/* Top Bar with History Toggle */}
            <style>{customStyles}</style>
            <div className="flex flex-col h-[100%] bg-gradient-to-br from-background via-background to-surface min-h-0 relative overflow-hidden max-h-[100vh]">


                {/* History Sidebar - full width on mobile, max 20rem on larger */}
                <div className={`fixed left-0 top-0 h-full w-[min(100vw,20rem)] max-w-[85vw] sm:max-w-none sm:w-80 bg-surface-elevated border-r border-border z-50 transform transition-transform duration-300 ${showHistory ? 'translate-x-0' : '-translate-x-full'
                    }`}>
                    <div className="flex flex-col h-full">
                        {/* Header */}
                        <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
                            <h2 className="text-base sm:text-lg font-bold text-foreground truncate">Conversations</h2>
                            <button
                                onClick={() => setShowHistory(false)}
                                className="p-2 rounded-lg hover:bg-surface transition-colors"
                            >
                                <FiX className="w-5 h-5 text-muted" />
                            </button>
                        </div>

                        {/* New Conversation Button */}
                        <div className="p-3 sm:p-4 border-b border-border">
                            <button
                                onClick={handleNewConversation}
                                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 sm:py-2 bg-primary text-on-solid rounded-lg hover:opacity-90 transition-opacity font-medium text-sm sm:text-base"
                            >
                                <FiPlus className="w-5 h-5 flex-shrink-0" />
                                <span className="truncate">New Conversation</span>
                            </button>
                        </div>

                        {/* Conversations List */}
                        <div className="flex-1 overflow-y-auto">
                            {isLoadingHistory ? (
                                <div className="p-4 text-center text-muted">Loading...</div>
                            ) : conversations.length === 0 ? (
                                <div className="p-4 text-center text-muted">No conversations yet</div>
                            ) : (
                                <div className="p-2">
                                    {conversations.map((conv) => (
                                        <div
                                            key={conv._id}
                                            onClick={() => handleConversationClick(conv._id)}
                                            className={`group p-3 rounded-lg mb-2 cursor-pointer transition-colors ${currentConversationId === conv._id
                                                ? 'bg-primary/20 border border-border'
                                                : 'hover:bg-surface border border-transparent'
                                                }`}
                                        >
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-foreground truncate">
                                                        {conv.title}
                                                    </h3>
                                                    {conv.preview && (
                                                        <p className="text-sm text-muted truncate mt-1">
                                                            {conv.preview}
                                                        </p>
                                                    )}
                                                    <div className="flex items-center gap-2 mt-2 text-xs text-muted">
                                                        <FiMessageSquare className="w-3 h-3" />
                                                        <span>{Number(conv.messageCount) / 2} messages</span>
                                                        <span>•</span>
                                                        <span>
                                                            {new Date(conv.lastActivityAt).toLocaleDateString()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={(e) => handleDeleteConversation(conv._id, e)}
                                                    className="ml-2 p-1.5 rounded hover:bg-surface transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100 touch-manipulation"
                                                    aria-label="Delete conversation"
                                                >
                                                    <FiTrash2 className="w-4 h-4 text-muted hover:text-danger" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Overlay when sidebar is open */}
                {showHistory && (
                    <div
                        className="fixed inset-0 bg-black/50 z-40"
                        onClick={() => setShowHistory(false)}
                    />
                )}


                {/* Main Content */}
                <div className="flex-1 flex flex-col min-h-0 relative">

                    {/* FIXED: Removed the max-h-[70vh] wrapper that was breaking the scroll container */}
                    {/* Starter: centered "What are you working on?" + Ask anything input (when no chat yet) */}
                    {showStarter && (
                        <div className="flex-1 flex flex-col items-center justify-center px-3 sm:px-4 py-6 sm:py-8 min-h-0 pt-6 sm:pt-8 md:mt-[5vh] lg:mt-[15vh]">
                            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-foreground text-center mb-6 sm:mb-10 max-w-[90vw]">
                                What are you working on{user?.name ? `, ${user?.name}` : ''}?
                            </h2>

                            <p className="text-xs font-medium text-muted text-center">Do you want to know about any stock?</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5 mt-3 sm:mt-4 w-full max-w-2xl">
                                {quickActionItems.map((item, idx) => (
                                    <button
                                        key={idx}
                                        type="button"
                                        onClick={() => handleSend(item.query)}
                                        disabled={isLoading}
                                        className="px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl bg-surface-elevated border border-border text-foreground text-xs sm:text-sm font-medium text-left hover:border-[color:var(--primary)] hover:bg-[var(--primary-5)] transition-all disabled:opacity-50 touch-manipulation"
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>


                            <div className="w-full max-w-2xl space-y-4 sm:space-y-6 mt-4 sm:mt-5 px-1">
                                <div className="flex items-center gap-2 sm:gap-3 bg-surface-elevated/90 dark:bg-neutral-900/90 border border-border rounded-xl sm:rounded-2xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-xl focus-within:border-[color:var(--primary)] focus-within:ring-2 focus-within:ring-[color:var(--primary-20)] transition-all">
                                    <button type="button" className="flex-shrink-0 p-2 rounded-xl text-muted hover:text-foreground hover:bg-surface transition-colors touch-manipulation" aria-label="Add attachment or new topic">
                                        <FiPlus className="w-5 h-5" />
                                    </button>
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                                        placeholder="Ask anything"
                                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-foreground placeholder-muted text-sm sm:text-base py-2"
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleSend()}
                                        disabled={isLoading || !input.trim()}
                                        className="flex-shrink-0 p-2.5 rounded-xl bg-[color:var(--primary)] text-[color:var(--on-solid)] hover:opacity-90 disabled:opacity-50 transition-opacity touch-manipulation"
                                        aria-label="Send"
                                    >
                                        <FiSend className="w-5 h-5" />
                                    </button>
                                </div>

                            </div>
                        </div>
                    )}

                    {/* Messages (when chat has started) */}
                    {!showStarter && (
                        <>
                            <div className="relative flex-1 min-h-0 flex flex-col max-h-[calc(100vh-160px)] sm:max-h-[calc(100vh-200px)] md:max-h-[calc(100vh-215px)]">
                                {/* FIXED: Added the ref and onScroll to the correct scrollable container */}
                                <div
                                    ref={messagesContainerRef}
                                    className="flex-1 overflow-y-auto overflow-x-hidden px-3 sm:px-4 md:px-8 py-4 sm:py-6 md:py-8 min-h-0"
                                    onScroll={handleScroll}
                                >
                                    {/* Loading more messages indicator */}
                                    {isLoadingMoreMessages && (
                                        <div className="text-center py-4 text-muted">
                                            <div className="inline-flex items-center gap-2">
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                                                Loading older messages...
                                            </div>
                                        </div>
                                    )}
                                    <div className="max-w-6xl mx-auto space-y-5 sm:space-y-8">
                                        {messages.map((message) => (
                                            <div
                                                key={message.id}
                                                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-fadeIn`}
                                            >
                                                <div className={`min-w-0 ${message.role === 'user'
                                                    ? 'max-w-[95%] sm:max-w-md md:max-w-lg bg-gradient-to-br from-primary via-primary to-accent text-white rounded-2xl sm:rounded-3xl rounded-tr-md shadow-2xl px-4 py-3 sm:px-5 sm:pb-2 sm:pt-4'
                                                    : 'max-w-[98%] sm:max-w-[95%] md:max-w-5xl bg-surface-elevated/80 backdrop-blur-xl text-foreground rounded-2xl sm:rounded-3xl rounded-tl-md px-4 py-4 sm:px-8 sm:py-6 shadow-xl border border-border/50'
                                                    }`}>
                                                    {message.isLoading && message.status !== 'streaming' ? (
                                                        <div className="flex items-center gap-4">
                                                            <div className="relative">
                                                                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary/30 border-t-primary"></div>
                                                                <div className="absolute inset-0 rounded-full bg-primary/20 animate-pulse"></div>
                                                            </div>
                                                            <div className="space-y-2">
                                                                <p className="font-bold text-foreground">
                                                                    {message.status === 'fetching_price'
                                                                        ? 'Fetching price...'
                                                                        : message.status === 'analyzing_charts'
                                                                            ? 'Analyzing data & charts...'
                                                                            : 'Processing market intelligence...'}
                                                                </p>
                                                                <div className="flex gap-2">
                                                                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                                                                    <div className="w-2 h-2 bg-accent rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                                                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            {message.role === 'assistant' ? (
                                                                <div className="flex items-start">
                                                                    <MessageContent content={message.displayedContent || message.content} />
                                                                    {message.status === 'streaming' && <StreamingCursor />}
                                                                </div>
                                                            ) : (
                                                                <div className="flex flex-col w-full">
                                                                    <p className="block w-full max-w-full whitespace-pre-wrap leading-relaxed font-medium text-left break-words">{message.content}</p>
                                                                </div>
                                                            )}

                                                            {/* Only show stock data after streaming is complete (not during streaming) */}
                                                            {message.stockData && message.stockData.length > 0 && !message.isLoading && message.status !== 'streaming' && (
                                                                <div className="mt-4 sm:mt-6 md:mt-8 space-y-4 sm:space-y-6 md:space-y-8">
                                                                    {/* Comparison table - full width */}
                                                                    <StockDetailsTable stocks={message.stockData} />

                                                                    {/* All stock cards in one row (wraps on narrow screens) */}
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 items-stretch rounded-xl sm:rounded-2xl">
                                                                        {message.stockData.map((stock, idx) => (
                                                                            <div key={idx} className="min-w-0">
                                                                                <StockCard stock={stock} isComparison={message.stockData!.length > 1} />
                                                                            </div>
                                                                        ))}
                                                                    </div>

                                                                    {/* Table charts: comparison or price/volume */}
                                                                    {message.stockData.length > 1 && message.chartData && (
                                                                        <div className="rounded-xl sm:rounded-2xl border border-border overflow-hidden bg-surface-elevated p-3 sm:p-4 min-w-0">
                                                                            <ChartWithExpand expandContent={<ComparisonChart stocks={message.stockData} chartData={message.chartData} />} title="Performance Comparison">
                                                                                <ComparisonChart stocks={message.stockData} chartData={message.chartData} />
                                                                            </ChartWithExpand>
                                                                        </div>
                                                                    )}
                                                                    {message.stockData.length === 1 && message.chartData?.[message.stockData[0].symbol] && (
                                                                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
                                                                            <div className="rounded-xl sm:rounded-2xl border border-border overflow-hidden bg-surface-elevated p-3 sm:p-4 min-w-0">
                                                                                <ChartWithExpand expandContent={<PriceChart data={message.chartData[message.stockData[0].symbol]} symbol={message.stockData[0].symbol} />} title={`${message.stockData[0].symbol} Price`}>
                                                                                    <PriceChart data={message.chartData[message.stockData[0].symbol]} symbol={message.stockData[0].symbol} />
                                                                                </ChartWithExpand>
                                                                            </div>
                                                                            <div className="rounded-xl sm:rounded-2xl border border-border overflow-hidden bg-surface-elevated p-3 sm:p-4 min-w-0">
                                                                                <ChartWithExpand expandContent={<VolumeChart data={message.chartData[message.stockData[0].symbol]} symbol={message.stockData[0].symbol} />} title={`${message.stockData[0].symbol} Volume`}>
                                                                                    <VolumeChart data={message.chartData[message.stockData[0].symbol]} symbol={message.stockData[0].symbol} />
                                                                                </ChartWithExpand>
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* TradingView charts at end - all in one row */}
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 lg:gap-6 items-stretch rounded-xl sm:rounded-2xl">
                                                                        {message.stockData.map((stock, idx) => (
                                                                            <div key={idx} className="min-w-0 rounded-xl overflow-hidden border border-border bg-surface-elevated shadow-sm relative">
                                                                                <ChartWithExpand expandContent={<TradingViewChart symbol={stock.symbol} title={message.stockData!.length > 1 ? stock.symbol : undefined} compact={false} />} title={message.stockData!.length > 1 ? stock.symbol : `Chart ${stock.symbol}`}>
                                                                                    <TradingViewChart
                                                                                        symbol={stock.symbol}
                                                                                        title={message.stockData!.length > 1 ? stock.symbol : undefined}
                                                                                        compact
                                                                                    />
                                                                                </ChartWithExpand>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}

                                                    <div className="flex items-center gap-2 mt-3 sm:mt-4">
                                                        <FiClock className={`w-3 h-3 flex-shrink-0 ${message.role === 'user' ? 'text-white/70' : 'text-muted'}`} />
                                                        <p className={`text-[10px] sm:text-xs font-medium ${message.role === 'user' ? 'text-white/70' : 'text-muted'}`}>
                                                            {message.timestamp.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).toLowerCase()}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {lastRelatedQuestions.length > 0 && !isLoading && (
                                <div className="space-y-2 flex flex-col items-center justify-center">
                                    <p className="text-xs font-semibold text-muted uppercase tracking-wider mb-2 text-center">Related questions</p>
                                    <ul className="flex flex-col gap-2 list-none p-0 m-0 w-full max-w-md items-center justify-center">
                                        {lastRelatedQuestions.map((q, qIdx) => (
                                            <li key={qIdx} className="w-full flex justify-center">
                                                <button
                                                    type="button"
                                                    onClick={() => handleSend(q)}
                                                    disabled={isLoading}
                                                    className="w-full max-w-md text-center px-3 py-2.5 rounded-xl bg-surface border border-border text-sm font-medium text-foreground hover:border-[color:var(--primary)] hover:bg-[var(--primary-5)] transition-all disabled:opacity-50 line-clamp-2"
                                                >
                                                    {q}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                </div>
                                {showScrollToBottom && (
                                    <button
                                        type="button"
                                        onClick={() => scrollToBottom('smooth')}
                                        className="absolute bottom-3 right-3 sm:bottom-4 sm:right-4 md:right-8 p-2 sm:p-2.5 rounded-xl bg-surface-elevated/95 backdrop-blur border border-border text-muted hover:text-foreground hover:bg-surface shadow-lg transition-all z-10 touch-manipulation"
                                        aria-label="Scroll to bottom"
                                    >
                                        <FiArrowDown className="w-5 h-5 sm:w-6 sm:h-6" />
                                    </button>
                                )}
                            </div>
                        </>
                    )}

                    {!showStarter && (() => {
                       
                        return (
                        <div className="border-t border-border/50 bg-transparent backdrop-blur-2xl px-3 sm:px-6 md:px-8 py-2.5 sm:py-3 shadow-2xl safe-area-pb">
                        <div className="max-w-5xl mx-auto space-y-4">
                            <div className="relative">
                                <div className="absolute -inset-1 bg-gradient-to-r from-primary via-accent to-primary rounded-2xl sm:rounded-3xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity"></div>
                                <div className="relative flex items-center gap-2 sm:gap-3 bg-surface/90 backdrop-blur-xl border-2 border-border/50 p-2.5 sm:p-3 rounded-2xl sm:rounded-3xl shadow-2xl focus-within:border-primary/50 focus-within:shadow-primary/20 transition-all">
                                    <button onClick={handleNewConversation} type="button" className="flex-shrink-0 p-2 rounded-xl text-muted hover:text-foreground hover:bg-surface transition-colors touch-manipulation" aria-label="New conversation">
                                        <FiPlus className="w-5 h-5" />
                                    </button>
                                    <input
                                        type="text"
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        placeholder="Ask about any stock..."
                                        className="flex-1 min-w-0 bg-transparent border-none outline-none text-foreground placeholder-muted text-sm sm:text-base py-2"
                                        disabled={isLoading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleSend()}
                                        disabled={isLoading || !input.trim()}
                                        className="flex-shrink-0 p-2.5 rounded-xl bg-[color:var(--primary)] text-[color:var(--on-solid)] hover:opacity-90 disabled:opacity-50 transition-opacity touch-manipulation"
                                        aria-label="Send"
                                    >
                                        <FiSend className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                        </div>
                        ); })()}
                </div>
            </div>
        </>
    );
};

export default StockAnalysisChatbot;